import {defineStore} from 'pinia';
import {autoPrintConfs, photoPrintConf} from '~/constants/projects.js';
import {getBatchPhotosetOptions} from '~/utils/pricing.js';
import {mapPromotions} from '~/utils/mapper-promotions.js';

function mapAutoPrintProjectTypes({projectTypes, promotions}) {
  return Object.keys(projectTypes).reduce((confs, productKey) => {
    const confSet = autoPrintConfs[productKey];

    if (confSet) {
      const mappedProjectTypes = mapPromotions({
        products: projectTypes[productKey],
        promotions
      });

      confSet.forEach(conf => {
        const productTypeConf = mappedProjectTypes.find(pt => pt.sku === conf.sku);

        if (productTypeConf && productTypeConf.sizes.length) {
          const startingSize = [...productTypeConf.sizes].sort((a, b) => a.price_cents - b.price_cents)[0];

          confs.push({
            ...conf,
            subtype: productTypeConf.subtype,
            productId: productTypeConf.id,
            promotionCode: productTypeConf.promotionCode,
            startingPrice: startingSize.price_cents,
            startingPromotionPrice: startingSize.promotionPrice
          });
        }
      });
    }

    return confs;
  }, []);
}

export const useProjectsStore = defineStore('projectsStore', {
  state: () => ({
    projectCategories: [],
    projectTypes: [],
    autoPrintProjectTypes: null,
    autoPrintProjectTypesSource: null,
    photoPrintsOrderStatus: null,
    photoPrintsOptions: null
  }),

  actions: {
    async getProjectCategories() {
      this.projectCategories = await useProjectsApi().fetchProjectCategories();
    },

    async getProjectTypes() {
      if (!this.projectTypes.length) {
        const projectTypesRes = await useProjectsApi().fetchProjectTypes();

        this.projectTypes = projectTypesRes.filter(item => item.visible)
          .sort((a, b) => {
            if (a.name < b.name) {
              return -1;
            }

            if (a.name > b.name) {
              return 1;
            }

            return 0;
          });
      }
    },

    async getAutoPrintProjectTypes() {
      if (this.autoPrintProjectTypes) {
        this.loadAutoPrintProjectTypePromotions();
        return this.autoPrintProjectTypes;
      }

      this.autoPrintProjectTypesSource = await useProjectsApi().fetchAutoPrintProjectTypes();
      this.autoPrintProjectTypes = mapAutoPrintProjectTypes({
        projectTypes: this.autoPrintProjectTypesSource
      });
      this.loadAutoPrintProjectTypePromotions();

      return this.autoPrintProjectTypes;
    },

    async loadAutoPrintProjectTypePromotions() {
      if (!this.autoPrintProjectTypesSource) {
        return;
      }

      try {
        const promotions = await usePricingStore().getPromotions();
        const projectTypes = mapAutoPrintProjectTypes({
          projectTypes: this.autoPrintProjectTypesSource,
          promotions
        });

        this.autoPrintProjectTypes.splice(0, this.autoPrintProjectTypes.length, ...projectTypes);
      } catch {
        // Base project types and prices remain usable when promotions are unavailable.
      }
    },

    async getPhotoPrintsOrderStatus() {
      const status = await useProjectsApi().getPhotoPrintsOrderStatus();
      this.photoPrintsOrderStatus = status;
      return status;
    },

    async getPhotoPrintsOptions() {
      if (!this.photoPrintsOptions) {
        const photoPrintsBatch = await useProjectsApi().getPhotoPrintBatch();
        const photoPrintProduct = await useProjectsApi().getPhotoPrintProduct({productId: photoPrintsBatch.product_id});

        this.photoPrintsOptions = getBatchPhotosetOptions(photoPrintsBatch, photoPrintProduct)

      }

      return this.photoPrintsOptions;
    }
  },

  getters: {
    containerAutoPrintProjectTypes: state => state.autoPrintProjectTypes && [...state.autoPrintProjectTypes.filter(pt => pt.isContainerPrint), photoPrintConf],
    singleFileAutoPrintProjectTypes: state => state.autoPrintProjectTypes?.filter(pt => !pt.isContainerPrint) //state.autoPrintProjectTypes && [...state.autoPrintProjectTypes.filter(pt => !pt.isContainerPrint), photoPrintConf]
  }
});
